import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError, api, setCsrfToken } from './api.ts';
import { AuditPanel } from './components/AuditPanel.tsx';
import { BookingCard } from './components/BookingCard.tsx';
import { ChallengePanel, type ChallengeStage } from './components/ChallengePanel.tsx';
import { SignIn } from './components/SignIn.tsx';
import { StatePanels, type AuthState } from './components/StatePanels.tsx';
import { StatusNotice, type Notice } from './components/StatusNotice.tsx';
import type { BookingView, ChallengeView, DemoConfig, Session } from './types.ts';

/**
 * The demo's orchestration.
 *
 * The flow this drives is: confirm a booking, get told verification is needed, receive a
 * code, answer it, receive a grant, present the grant to confirm. Two properties of the
 * implementation are worth defending if this file is rewritten.
 *
 * - **The grant token lives in a `useRef`, not in state and not in storage.** It is a
 *   credential with a 300-second life that is used once, moments after it is issued.
 *   Putting it in `localStorage` would make it survive the tab, and putting it in React
 *   state would mean it appears in any component tree dump. Neither is worth the
 *   convenience. What *is* in state is the pair of non-secret facts the panels render —
 *   which booking the grant authorizes and when it lapses — so nothing during render has
 *   to reach for the token, and the ref holds exactly one thing: the secret.
 * - **Nothing here decides whether step-up is required.** `booking.stepUpRequired` is
 *   rendered because the server sent it; the actual gate is the server refusing to
 *   confirm without a grant. If this file were rewritten to skip the challenge, the
 *   confirmation would simply fail.
 */

type View = 'loading' | 'signed-out' | 'bookings' | 'challenge';

export function App() {
  const [view, setView] = useState<View>('loading');
  const [config, setConfig] = useState<DemoConfig | undefined>();
  const [session, setSession] = useState<Session | undefined>();
  const [bookings, setBookings] = useState<readonly BookingView[]>([]);
  const [audit, setAudit] = useState<Awaited<ReturnType<typeof api.audit>>['events']>([]);
  const [notice, setNotice] = useState<Notice | undefined>();
  const [busy, setBusy] = useState(false);

  const [activeBookingId, setActiveBookingId] = useState<string | undefined>();
  const [transactionId, setTransactionId] = useState<string | undefined>();
  const [challenge, setChallenge] = useState<ChallengeView | undefined>();
  const [challengeStage, setChallengeStage] = useState<ChallengeStage>('sending');
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  /**
   * Focus lands on the booking-list heading whenever the flow returns to it.
   *
   * `ChallengePanel` moves focus forward as its own steps change; this is the other half.
   * Leaving the challenge removes the button that was focused, so without this a
   * keyboard-only user is dropped onto `<body>` and has to tab in from the masthead again
   * — and a screen reader user hears the live region say "booking confirmed" while their
   * cursor sits several steps back on a control that no longer exists.
   *
   * The counter, rather than a boolean, is what makes a second return to the same view
   * fire again.
   */
  const listHeadingRef = useRef<HTMLHeadingElement>(null);
  const [returnToList, setReturnToList] = useState(0);

  // The token, and nothing but the token. Held out of state on purpose: see the note above.
  const grantTokenRef = useRef<string | undefined>(undefined);
  /**
   * The renderable half of a held grant: which booking it authorizes, and when it lapses.
   *
   * Split from the token deliberately. Reading a ref during render is a React correctness
   * hazard as well as a lint error — a ref change does not schedule a re-render, so the
   * grant countdown would only move when something else happened to re-render the tree.
   * Keeping the displayed facts in state fixes that, and keeping the token out of state
   * preserves the property the split exists for.
   */
  const [heldGrant, setHeldGrant] = useState<
    { bookingId: string; expiresAt: number } | undefined
  >();
  const [grantSpent, setGrantSpent] = useState(false);
  /** Content hash the held grant attests to, so the UI can show the binding break. */
  const [boundHash, setBoundHash] = useState<string | undefined>();

  // One ticking clock for both countdowns. Two intervals would drift apart on screen.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (returnToList > 0) listHeadingRef.current?.focus();
  }, [returnToList]);

  const refreshBookings = useCallback(async () => {
    const { bookings: list } = await api.bookings();
    setBookings(list);
  }, []);

  const refreshAudit = useCallback(async () => {
    try {
      const { events } = await api.audit();
      setAudit(events);
    } catch {
      // The trail is a panel, not the flow. A failure to read it must not derail the
      // action the customer just completed.
    }
  }, []);

  // Resume an existing session on load, so a page reload does not look like a sign-out.
  useEffect(() => {
    void (async () => {
      try {
        setConfig(await api.config());
      } catch {
        setNotice({
          tone: 'refused',
          heading: 'Cannot reach the server',
          detail: 'Start the BFF with `npm run bff:local`, then reload this page.',
        });
        setView('signed-out');
        return;
      }

      try {
        const resumed = await api.session();
        setCsrfToken(resumed.csrfToken);
        setSession(resumed);
        await refreshBookings();
        await refreshAudit();
        setView('bookings');
      } catch {
        setView('signed-out');
      }
    })();
  }, [refreshBookings, refreshAudit]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const established = await api.signIn(email, password);
      setCsrfToken(established.csrfToken);
      setSession(established);
      await refreshBookings();
      await refreshAudit();
      setNotice(undefined);
      setView('bookings');
    },
    [refreshBookings, refreshAudit],
  );

  /**
   * Sign out. **Resolves rather than rejects**, which is why the call site can `void` it.
   *
   * The server call is an external boundary and can fail. The local state is discarded
   * either way: a page that stays visibly signed in because the network dropped is worse
   * than one that says the server was not reached. The cookie is the server's to expire,
   * and the notice says so rather than implying a clean revocation.
   */
  const signOut = useCallback(async () => {
    let failed = false;
    let failure: unknown;
    try {
      await api.signOut();
    } catch (caught) {
      failed = true;
      failure = caught;
    }

    setCsrfToken(undefined);
    grantTokenRef.current = undefined;
    setSession(undefined);
    setBookings([]);
    setAudit([]);
    setHeldGrant(undefined);
    setGrantSpent(false);
    setBoundHash(undefined);
    setTransactionId(undefined);
    setChallenge(undefined);
    setView('signed-out');

    if (!failed) {
      setNotice(undefined);
      return;
    }
    setNotice({
      tone: 'signal',
      heading: 'Signed out locally',
      detail:
        'The server did not confirm the sign-out, so its session cookie may stay live until it expires. Everything this page was holding has been discarded.',
      ...(failure instanceof ApiError
        ? { code: failure.code, correlationId: failure.correlationId }
        : {}),
    });
  }, []);

  /** Report a failure using the server's own code and correlation id. */
  const reportError = useCallback((caught: unknown, heading: string) => {
    if (caught instanceof ApiError) {
      setNotice({
        tone: 'refused',
        // A refused grant is the demo's security-failure beat, so it gets a heading that
        // names what happened. The server's message is deliberately identical for replay,
        // expiry, tampering and cross-customer use, and that is not softened here: the
        // heading describes the class of failure, the audit panel beside it is where the
        // specific reason lives.
        heading: caught.code === 'STEP_UP_GRANT_INVALID' ? 'Verification refused' : heading,
        detail: caught.message,
        code: caught.code,
        correlationId: caught.correlationId,
      });
      return;
    }
    setNotice({
      tone: 'refused',
      heading,
      detail: 'Something went wrong before the server answered. Try again.',
    });
  }, []);

  const startChallenge = useCallback(
    async (openTransactionId: string) => {
      try {
        const { challenge: view } = await api.startChallenge(openTransactionId);
        setChallenge(view);
        setChallengeStage('awaiting-code');
        setNotice(
          view.delivered
            ? undefined
            : {
                tone: 'neutral',
                heading: 'A code was already on its way',
                detail:
                  'Reloading did not send a second code, so the one already in your inbox is still the right one.',
              },
        );
      } catch (caught) {
        // Branch on the server's own code, never on message text. The three failures that
        // can happen here need three different things said, and the code is the only
        // field stable enough to switch on.
        setChallengeStage(stageForChallengeFailure(caught));
        reportError(caught, 'Could not send a verification code');
      } finally {
        await refreshAudit();
      }
    },
    [refreshAudit, reportError],
  );

  /**
   * Confirm a booking, presenting a grant if one is held for exactly this booking.
   *
   * The `bookingId` check on the held grant is not the security control — the server
   * rejects a mismatched grant regardless. It is here so the demo does not send a grant
   * to a booking it was not minted for and then explain away the refusal.
   *
   * `presentedToken` exists for the one caller that has a grant this callback cannot yet
   * see: `answerChallenge` confirms immediately after verification, and the state it just
   * set does not reach this closure until the next render. Passing the token explicitly is
   * the fix; reading it from a ref to dodge the same problem is what put the token in a
   * ref-during-render position to begin with.
   */
  const confirmBooking = useCallback(
    async (bookingId: string, presentedToken?: string) => {
      setBusy(true);
      setNotice(undefined);
      try {
        const token =
          presentedToken ??
          (heldGrant?.bookingId === bookingId ? grantTokenRef.current : undefined);

        const outcome = await api.confirm(bookingId, token);

        if (outcome.status === 'CONFIRMED') {
          if (token) {
            grantTokenRef.current = undefined;
            setHeldGrant(undefined);
            setGrantSpent(true);
          }
          setNotice({
            tone: 'sealed',
            heading: 'Booking confirmed',
            detail: token
              ? 'The verification you completed was consumed to authorize this exact booking. It cannot be used again.'
              : 'This booking is at or below the threshold, so no extra verification was needed.',
          });
          setActiveBookingId(bookingId);
          setChallenge(undefined);
          setTransactionId(undefined);
          setView('bookings');
          setReturnToList((count) => count + 1);
          await refreshBookings();
          await refreshAudit();
          return;
        }

        // The server demanded verification. Open the challenge for this booking.
        setActiveBookingId(bookingId);
        setTransactionId(outcome.transactionId);
        setChallengeStage('sending');
        setChallenge(undefined);
        setView('challenge');
        await startChallenge(outcome.transactionId);
      } catch (caught) {
        reportError(caught, 'Could not confirm this booking');
        await refreshBookings();
        await refreshAudit();
      } finally {
        setBusy(false);
      }
    },
    [heldGrant, refreshBookings, refreshAudit, reportError, startChallenge],
  );

  const answerChallenge = useCallback(
    async (code: string) => {
      if (!transactionId || !activeBookingId) return;
      setBusy(true);
      setNotice(undefined);
      try {
        const result = await api.answerChallenge(transactionId, code);

        switch (result.status) {
          case 'VERIFIED': {
            grantTokenRef.current = result.grantToken;
            setHeldGrant({
              bookingId: result.bookingId,
              expiresAt: result.grantExpiresAt,
            });
            setGrantSpent(false);
            setBoundHash(
              bookings.find((booking) => booking.bookingId === result.bookingId)?.contentHashPrefix,
            );
            setChallengeStage('verified');
            await refreshAudit();
            // Straight on to the confirmation the customer asked for. The grant exists
            // to authorize this one action, so making them press a second button would
            // only widen the window in which it can go stale. The token is handed over
            // explicitly because the state set two lines above is not visible to the
            // callback closure until the next render.
            await confirmBooking(result.bookingId, result.grantToken);
            return;
          }

          case 'RETRY':
            setChallenge((current) =>
              current
                ? {
                    ...current,
                    attemptsRemaining: result.attemptsRemaining,
                    expiresAt: result.expiresAt,
                  }
                : current,
            );
            setNotice({
              tone: 'refused',
              heading: 'That code is not correct',
              detail: `Check the most recent email and try again. ${result.attemptsRemaining} attempt${
                result.attemptsRemaining === 1 ? '' : 's'
              } left.`,
              code: 'CHALLENGE_INVALID_ANSWER',
            });
            break;

          case 'LOCKED':
            setChallengeStage('locked');
            setNotice({
              tone: 'refused',
              heading: 'Verification closed',
              detail:
                'Too many incorrect attempts. Your booking has not changed and no charge was made.',
              code: 'CHALLENGE_LOCKED',
            });
            break;

          case 'EXPIRED':
          default:
            setChallengeStage('expired');
            setNotice({
              tone: 'refused',
              heading: 'That code expired',
              detail: 'Request a new code. Your booking has not changed.',
              code: 'CHALLENGE_EXPIRED',
            });
            break;
        }
      } catch (caught) {
        reportError(caught, 'Could not check that code');
        // Same mapping as a failed `start`, and for a reason worth stating: the booking
        // can move during the challenge window, and grant issuance re-checks the content
        // hash, so `BOOKING_CHANGED` arrives here as well as there. Hardcoding `expired`
        // would tell a customer whose booking was amended to ask for a new code, which
        // would fail for the same reason the first one did.
        setChallengeStage(stageForChallengeFailure(caught));
      } finally {
        setBusy(false);
        await refreshAudit();
      }
    },
    [transactionId, activeBookingId, bookings, confirmBooking, refreshAudit, reportError],
  );

  /**
   * Ask the server to deliver the code again.
   *
   * The cooldown and the ceiling are both server-held; this only renders them. The button
   * is disabled while the cooldown runs, so reaching a refusal here means the countdown and
   * the server disagreed — which is exactly when the error path has to say something
   * useful, hence branching on the server's code rather than assuming success.
   *
   * A resend deliberately does not touch `attemptsRemaining`: the server sends the
   * transaction's count, and it does not go back up.
   */
  const resendCode = useCallback(async () => {
    if (!transactionId) return;
    setBusy(true);
    setNotice(undefined);
    try {
      const { challenge: view } = await api.resendChallenge(transactionId);
      setChallenge(view);
      setNotice({
        tone: 'neutral',
        heading: 'A new code is on its way',
        detail:
          'The previous code no longer works, so use the newest email. Your remaining attempts are unchanged.',
      });
    } catch (caught) {
      // A delivery failure is the one refusal that ends the step; the cooldown and the
      // ceiling both leave the customer with a code they can still enter, so the panel
      // stays where it is and only the notice changes.
      if (caught instanceof ApiError && caught.code === 'CHALLENGE_DELIVERY_FAILED') {
        setChallengeStage('delivery-failed');
      } else if (caught instanceof ApiError && caught.code === 'CHALLENGE_EXPIRED') {
        setChallengeStage('expired');
      }
      reportError(caught, 'Could not send another code');
    } finally {
      setBusy(false);
      await refreshAudit();
    }
  }, [transactionId, refreshAudit, reportError]);

  /**
   * Amend a booking. Exists so the tampering scenario can be performed, not described.
   *
   * A held grant is deliberately **not** discarded here. The server will refuse it
   * because the content hash no longer matches, and the demo is stronger for showing
   * that refusal than for the browser quietly tidying up beforehand.
   */
  const amendBooking = useCallback(
    async (bookingId: string, amountMinor: number) => {
      setBusy(true);
      setNotice(undefined);
      try {
        await api.amend(bookingId, { amountMinor });
        await refreshBookings();
        await refreshAudit();
        setNotice(
          heldGrant?.bookingId === bookingId
            ? {
                tone: 'signal',
                heading: 'Booking changed after verification',
                detail:
                  'The content hash moved, so the verification you completed no longer applies to this booking. Confirming it will be refused.',
              }
            : {
                tone: 'neutral',
                heading: 'Booking updated',
                detail: 'The content hash changed, because the hash covers the amount.',
              },
        );
      } catch (caught) {
        reportError(caught, 'Could not change this booking');
      } finally {
        setBusy(false);
      }
    },
    [heldGrant, refreshBookings, refreshAudit, reportError],
  );

  const leaveChallenge = useCallback(() => {
    setView('bookings');
    setChallenge(undefined);
    setTransactionId(undefined);
    setNotice(undefined);
    setReturnToList((count) => count + 1);
  }, []);

  const restartChallenge = useCallback(async () => {
    if (!activeBookingId) return;
    setChallenge(undefined);
    setTransactionId(undefined);
    await confirmBooking(activeBookingId);
  }, [activeBookingId, confirmBooking]);

  const activeBooking = useMemo(
    () => bookings.find((booking) => booking.bookingId === activeBookingId),
    [bookings, activeBookingId],
  );

  const codeSecondsRemaining = challenge ? Math.max(0, challenge.expiresAt - now) : 0;
  // Off the same tick as the code countdown, for the same reason: two intervals drift
  // apart on screen, and these two numbers are read together.
  const resendSecondsRemaining = challenge ? Math.max(0, challenge.resendAvailableAt - now) : 0;

  const authState: AuthState = useMemo(
    () => ({
      stepUp: describeStepUp(view, challengeStage, activeBooking, grantSpent),
      transactionId,
      attemptsRemaining: challenge?.attemptsRemaining,
      secondsRemaining: challenge ? codeSecondsRemaining : undefined,
      grantHeld: heldGrant !== undefined,
      grantSecondsRemaining: heldGrant ? Math.max(0, heldGrant.expiresAt - now) : undefined,
      grantSpent,
    }),
    [
      view,
      challengeStage,
      activeBooking,
      transactionId,
      challenge,
      codeSecondsRemaining,
      heldGrant,
      grantSpent,
      now,
    ],
  );

  if (view === 'loading') {
    return (
      <main className="signin">
        <p className="eyebrow">AnyCompany Hotels</p>
        <p className="muted">Loading…</p>
      </main>
    );
  }

  if (view === 'signed-out' || !session || !config) {
    return (
      <>
        <div className="signin">
          <StatusNotice notice={notice} />
        </div>
        <SignIn onSignIn={signIn} stage={config?.stage ?? 'local'} />
      </>
    );
  }

  return (
    <>
      <header className="masthead">
        <h1>AnyCompany Hotels</h1>
        <span className="masthead__rule" />
        <span className="masthead__stage">stage {config.stage}</span>
        <span className="value muted">{session.maskedEmail}</span>
        <button type="button" className="button--quiet" onClick={() => void signOut()}>
          Sign out
        </button>
      </header>

      <div className="layout">
        <main className="column" aria-labelledby="customer-heading">
          {/*
            A real heading rather than a styled paragraph, so the document outline is
            h1 → h2 and a screen reader user can jump to it. `tabIndex={-1}` makes it
            programmatically focusable without adding it to the tab sequence.
          */}
          <h2 className="eyebrow" id="customer-heading" ref={listHeadingRef} tabIndex={-1}>
            Your bookings
          </h2>

          <StatusNotice notice={notice} />

          {view === 'challenge' ? (
            <ChallengePanel
              challenge={challenge}
              stage={challengeStage}
              secondsRemaining={codeSecondsRemaining}
              otpLength={challenge?.otpLength ?? config.otpLength}
              otpTtlSeconds={config.otpTtlSeconds}
              busy={busy}
              resendSecondsRemaining={resendSecondsRemaining}
              onSubmit={(code) => void answerChallenge(code)}
              onResend={() => void resendCode()}
              onRestart={() => void restartChallenge()}
              onCancel={leaveChallenge}
            />
          ) : bookings.length === 0 ? (
            <p className="muted">
              No bookings on this account. Run <code className="value">npm run seed</code> to create
              the synthetic ones.
            </p>
          ) : (
            bookings.map((booking) => (
              <BookingCard
                key={booking.bookingId}
                booking={booking}
                binding={bindingFor(booking, boundHash, heldGrant?.bookingId)}
                busy={busy}
                challenging={false}
                onConfirm={(bookingId) => void confirmBooking(bookingId)}
                onAmend={(bookingId, amountMinor) => void amendBooking(bookingId, amountMinor)}
              />
            ))
          )}
        </main>

        <aside className="column column--instrument" aria-label="Server-held state">
          <StatePanels
            session={session}
            config={config}
            booking={activeBooking ?? bookings[0]}
            challenge={challenge}
            auth={authState}
          />
          <AuditPanel events={audit} onRefresh={() => void refreshAudit()} busy={busy} />
        </aside>
      </div>
    </>
  );
}

/**
 * Which recoverable state a failed challenge call puts the panel into.
 *
 * `BOOKING_CHANGED` and `CHALLENGE_DELIVERY_FAILED` are the two the server can raise with
 * something specific to say. Everything else — a closed transaction, a session that
 * lapsed — reduces to "start again", which is what `expired` already advises, so it is the
 * right default rather than a placeholder.
 *
 * Exported for the unit tests: this is the whole of the mapping from a server error code
 * to what the customer is told, and it is worth pinning directly rather than only through
 * the four flows that happen to reach it.
 */
export function stageForChallengeFailure(caught: unknown): ChallengeStage {
  if (!(caught instanceof ApiError)) return 'expired';
  switch (caught.code) {
    case 'BOOKING_CHANGED':
      return 'booking-changed';
    case 'CHALLENGE_DELIVERY_FAILED':
      return 'delivery-failed';
    default:
      return 'expired';
  }
}

function describeStepUp(
  view: View,
  stage: ChallengeStage,
  booking: BookingView | undefined,
  grantSpent: boolean,
): AuthState['stepUp'] {
  if (view === 'challenge') {
    if (stage === 'locked') return 'locked';
    if (stage === 'expired' || stage === 'delivery-failed') return 'expired';
    if (stage === 'booking-changed') return 'changed';
    if (stage === 'verified') return 'verified';
    return 'in progress';
  }
  if (grantSpent) return 'verified';
  if (booking?.stepUpRequired && booking.status !== 'CONFIRMED') return 'required';
  return 'not required';
}

/**
 * Whether this booking's hash is bound to a held grant, and whether that binding still
 * holds.
 *
 * `broken` is the interesting case: a grant is held for this booking, but the content has
 * moved since it was issued, so the hash on screen is no longer the one the grant
 * attests to.
 */
export function bindingFor(
  booking: Pick<BookingView, 'bookingId' | 'contentHashPrefix'>,
  boundHash: string | undefined,
  grantBookingId: string | undefined,
): 'none' | 'bound' | 'broken' {
  if (grantBookingId !== booking.bookingId || boundHash === undefined) return 'none';
  return booking.contentHashPrefix === boundHash ? 'bound' : 'broken';
}
